import type { SVGProps } from "react";

/**
 * Icons used only by the kanban screen.
 *
 * Line icons on a 24-unit grid sized in `em`, so a caller sets the size with
 * font-size and the icon follows whatever `currentColor` it lands in — the same
 * contract the tasks and workout icon sets use.
 */

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      width="1em"
      height="1em"
      {...props}
    >
      {children}
    </svg>
  );
}

/** A board of lanes — used when there is no board to show. */
export function BoardIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="6" height="11" rx="1.6" />
      <rect x="15" y="4" width="6" height="16" rx="1.6" />
    </Icon>
  );
}

/** A magnifier — used when a filter matches nothing. */
export function KanbanSearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="10.75" cy="10.75" r="6.25" />
      <path d="m15.4 15.4 4.1 4.1" />
    </Icon>
  );
}

/**
 * A person, marking the owner on a card's meta line.
 *
 * UI-REVIEW's minor list: "Owner renders as unlabelled grey text, visually
 * identical to an overflow tag." On a line that already carries `#tags` in the
 * same size and colour, a name needs a mark of its own — six pixels of glyph is
 * cheaper than the word "Owner" and unambiguous at a glance.
 */
export function OwnerIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 19.5a7 7 0 0 1 14 0" />
    </Icon>
  );
}

/** A picture — the card carries attachments, without showing them on the tile. */
export function KanbanImageIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="m4.5 17 4.2-4.2a2 2 0 0 1 2.8 0l3.4 3.4m0 0 1.6-1.6a2 2 0 0 1 2.8 0l1.2 1.2m-5.6.4 2 2" />
    </Icon>
  );
}
